import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Lock } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join(" ") : "Login failed";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="login-page" className="pt-24 pb-16 min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-navy-light border border-gold/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock size={24} className="text-gold" />
          </div>
          <p className="font-cursive text-gold text-3xl mb-2">Noor Realtor</p>
          <h1 className="font-heading text-2xl text-beige">Admin Login</h1>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="login-form"
          className="bg-navy-light border border-white/10 rounded-sm p-8 space-y-5"
        >
          {error && (
            <div data-testid="login-error" className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 rounded-sm">
              {error}
            </div>
          )}

          <div>
            <label className="text-xs text-beige/50 mb-1.5 block tracking-wider uppercase">Email</label>
            <Input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@noorrealtor.com"
              className="bg-navy border-white/10 text-beige placeholder:text-beige/30 rounded-sm h-11 focus:border-gold/50"
              required
            />
          </div>

          <div>
            <label className="text-xs text-beige/50 mb-1.5 block tracking-wider uppercase">Password</label>
            <Input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="bg-navy border-white/10 text-beige placeholder:text-beige/30 rounded-sm h-11 focus:border-gold/50"
              required
            />
          </div>

          <button
            type="submit"
            data-testid="login-form-submit-button"
            disabled={submitting}
            className="w-full bg-gold hover:bg-gold-hover text-navy font-bold text-sm tracking-widest uppercase px-6 py-4 transition-colors duration-300 disabled:opacity-50"
          >
            {submitting ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
